import './TrendChart.css';
import type { RefObject } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { useReducedMotion } from '../../a11y/useReducedMotion';
import { Card } from '../../components/Card';
import { CardHeader } from '../../components/CardHeader';
import { t } from '../../copy/strings';
import { tMinAt } from '../../format/time';
import { isPreHeadline } from '../../model/headline';
import type { Candidate, HistoryPoint } from '../../model/types';
import { useHeadline, useLayout, useStatics, useTimeline, useView } from '../../state/hooks';
import { partyClass, partyOf } from '../../theme/parties';
import {
  crossingPoint,
  hasCurve,
  lastLeadChange,
  MIN_PLOT_H,
  type TrendSeries,
  trendAria,
  trendFrame,
  trendGrid,
  trendPoints,
  trendSeries,
  xOf,
  yOf,
} from './model';

/** id do título (o `Card` aponta `aria-labelledby` para ele, §15.4.1). */
export const TREND_TITLE_ID = 'trend-title';

export interface TrendChartProps {
  /**
   * Histórico nacional (`historySeries`, §14.8.10: `history.json` ∪ um ponto por snapshot aceito, por `seq`). A ligação
   * em `src/app/` o passa: `features/` lê só hooks e ações, e não há hook do histórico (FE §6.7).
   */
  points: readonly HistoryPoint[];
  /** `card` (padrão): cartão "grow" com título. `embedded`: dentro do `ProbabilitySlot` (§7.14), sem cartão nem título. */
  variant?: 'card' | 'embedded';
}

/** Caixa da área do gráfico em px de layout (o `viewBox` do SVG), medida por `ResizeObserver` (§7.13 bloco 2). */
function usePlotSize(ref: RefObject<HTMLElement>, on: boolean): { w: number; h: number } {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!on || !el) return;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setSize((s) => (s.w === w && s.h === h ? s : { w, h }));
    };
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [on]);
  return size;
}

/**
 * "Ao longo da apuração" (§7.13): fatia de cada candidato conforme as seções foram totalizadas, em SVG feito à mão
 * (sem biblioteca), com o começo pontilhado até 5% e a marca da última mudança de liderança. `<figure>` + `<svg
 * role="img">` com o nome `trend.aria` atualizado a cada snapshot, sem `aria-live` (§15.4.2); tudo dentro do SVG é
 * `aria-hidden`. Sem interação (sem tooltip, hover nem zoom). Legenda e séries sempre na ordem 13 | 22 (N01), as
 * mesmas classes para os dois lados (só `party-{n}` muda). Replay: curva cortada no instante exibido, sem desenho de
 * trecho novo; movimento reduzido: sem desenho, sem anéis, pontos finais sem deslizar (§7.16, §15.6).
 * Não monta em `data-h` short/tiny (nem no tablet e no `land`, que são short); o `ProbabilitySlot` usa `embedded`.
 */
export function TrendChart({ points, variant = 'card' }: TrendChartProps) {
  const view = useView();
  const statics = useStatics();
  const { mode, heightClass } = useLayout();
  const { live } = useTimeline();
  const pre = isPreHeadline(useHeadline());
  const reduced = useReducedMotion();
  const hidden = variant === 'card' && (heightClass === 'short' || heightClass === 'tiny');
  const plotRef = useRef<HTMLElement>(null);
  const size = usePlotSize(plotRef, !hidden);
  const lastSeqRef = useRef<number | null>(null);
  const freshSeqRef = useRef<number | null>(null);
  if (hidden) return null;

  const candidates: readonly [Candidate, Candidate] | null = view?.candidates ?? statics.candidates?.president ?? null;
  // Antes das 17h00 e sem snapshot: estado vazio, qualquer que seja o payload (E01, AC-7.17-03).
  const instant = view?.instantTMin ?? Number.NEGATIVE_INFINITY;
  const pts = pre || view === null ? [] : trendPoints(points, instant);
  const curve = hasCurve(pts);

  // Trecho novo (A14): o último ponto chegou ao vivo agora. Fica marcado até o próximo ponto (a animação não reinicia
  // num re-render) e some no replay, com movimento reduzido e quando a série encolhe (rollback, troca de instante).
  const last = pts.length > 0 ? (pts[pts.length - 1]?.seq ?? null) : null;
  const prev = lastSeqRef.current;
  if (!live || reduced) freshSeqRef.current = null;
  else if (last !== null && prev !== null && last > prev) freshSeqRef.current = last;
  else if (last === null || (prev !== null && last < prev)) freshSeqRef.current = null;
  lastSeqRef.current = last;
  const fresh = curve && live && !reduced && freshSeqRef.current !== null && freshSeqRef.current === last;

  const change = view && curve ? lastLeadChange(view.events, instant) : null;
  const cross = curve ? crossingPoint(pts, change) : null;
  const rings =
    cross !== null && change !== null && live && !reduced && instant - tMinAt(Date.parse(change.occurred_at)) <= 1;

  const draw = curve && candidates !== null && size.w > 0 && size.h >= MIN_PLOT_H;
  let svg = null;
  if (draw && candidates) {
    const fr = trendFrame(pts, size.w, size.h);
    const series: [TrendSeries, TrendSeries] = [trendSeries(fr, pts, 0, fresh), trendSeries(fr, pts, 1, fresh)];
    const grid = fr.axes ? trendGrid(fr) : [];
    const baseY = size.h - 3;
    svg = (
      <svg
        class="trend-chart__svg"
        width={size.w}
        height={size.h}
        viewBox={`0 0 ${size.w} ${size.h}`}
        role="img"
        aria-label={trendAria(candidates, pts, cross)}
        data-dataviz=""
      >
        {/* biome-ignore lint/a11y/noAriaHiddenOnFocusable: nada dentro do SVG é focável; §7.13 bloco 7 pede os elementos internos aria-hidden */}
        <g class="trend-chart__layers" aria-hidden="true">
          {fr.axes ? (
            <g class="trend-chart__axes">
              {grid.map((g) => (
                <line key={`g${g.label}`} class="trend-chart__grid" x1={fr.ml} x2={size.w - fr.mr} y1={g.y} y2={g.y} />
              ))}
              {grid.map((g) => (
                <text key={`y${g.label}`} class="trend-chart__tick" x={28} y={g.y} text-anchor="end" dy="0.35em">
                  {g.label}
                </text>
              ))}
              <text class="trend-chart__tick" x={fr.ml} y={baseY} text-anchor="start">
                {t('trend.axis.start')}
              </text>
              <text class="trend-chart__tick" x={xOf(fr, 0.5)} y={baseY} text-anchor="middle">
                {t('trend.axis.mid')}
              </text>
              <text class="trend-chart__tick" x={xOf(fr, 1)} y={baseY} text-anchor="end">
                {t('trend.axis.end')}
              </text>
            </g>
          ) : null}
          {series.map((s) => (
            <g
              key={s.side}
              class={`trend-chart__series ${partyClass(partyOf(candidates[s.side]))}`}
              data-number={candidates[s.side].number}
            >
              {s.dotted ? <path class="trend-chart__dotted" d={s.dotted} stroke-dasharray="2 4" /> : null}
              {s.solid ? <path class="trend-chart__solid" d={s.solid} /> : null}
              {s.fresh ? <path key={`f${last}`} class="trend-chart__fresh" d={s.fresh} pathLength={1} /> : null}
            </g>
          ))}
          {cross ? (
            <g class="trend-chart__change">
              {rings && change ? (
                <g key={change.id}>
                  <circle class="trend-chart__ring" cx={xOf(fr, cross.f)} cy={yOf(fr, cross.share)} r={5} />
                  <circle
                    class="trend-chart__ring trend-chart__ring--late"
                    cx={xOf(fr, cross.f)}
                    cy={yOf(fr, cross.share)}
                    r={5}
                  />
                </g>
              ) : null}
              <circle class="trend-chart__mark" cx={xOf(fr, cross.f)} cy={yOf(fr, cross.share)} r={5} />
            </g>
          ) : null}
          {series.map((s) => (
            <circle
              key={`e${s.side}`}
              class={`trend-chart__end ${partyClass(partyOf(candidates[s.side]))}`}
              r={3.5}
              style={{ transform: `translate(${s.end.x}px, ${s.end.y}px)` }}
            />
          ))}
        </g>
      </svg>
    );
  }

  const legend =
    candidates && mode !== 'tv' ? (
      <ul class="trend-chart__legend">
        {candidates.map((c) => (
          <li key={c.number} class={`trend-chart__key ${partyClass(partyOf(c))}`} data-number={c.number}>
            <span class="trend-chart__swatch" data-dataviz="" aria-hidden="true" />
            {c.display_name}
          </li>
        ))}
      </ul>
    ) : null;

  const plot = (
    <figure class="trend-chart__plot" ref={plotRef} data-state={curve ? 'curve' : 'empty'}>
      {curve ? svg : <p class="trend-chart__empty">{t('trend.empty')}</p>}
    </figure>
  );

  const still = !live || reduced ? ' trend-chart--still' : '';
  if (variant === 'embedded') {
    return (
      <div class={`trend-chart trend-chart--embedded${still}`} data-testid="trend-chart">
        {legend}
        {plot}
      </div>
    );
  }
  return (
    <Card
      variant={mode === 'flow' ? undefined : 'grow'}
      class={`trend-chart${still}`}
      data-testid="trend-chart"
      labelledBy={TREND_TITLE_ID}
    >
      <CardHeader title={t('trend.title')} titleId={TREND_TITLE_ID} />
      {legend}
      {plot}
    </Card>
  );
}
